import { useStore } from '@hooks/useStore'
import { Search } from '@mui/icons-material'
import { Autocomplete, Box, CircularProgress, Stack, TextField, Typography } from '@mui/material'
import { IKvkCompany } from '@api'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { ISingleCompany } from '@typings'
import { useKvkPrefilled } from '../client/client.kvk.search'

export const CompanyKvkSearch = observer(() => {
  const { kvk } = useStore()
  const { setValue, getValues, reset } = useFormContext<{ company: ISingleCompany }>()
  const { markFieldAsPrefilled } = useKvkPrefilled()
  const [inputValue, setInputValue] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedValue, setSelectedValue] = useState<IKvkCompany | null>(null)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputValue) {
        setSearchQuery(inputValue)
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [inputValue])

  // Trigger search when query changes
  useEffect(() => {
    if (searchQuery) {
      kvk.searchCompany(searchQuery)
    }
  }, [searchQuery])

  const handleSelectCompany = (company: IKvkCompany | null) => {
    if (!company) return

    // Pre-fill form fields with KVK data and mark them as prefilled
    if (company.name) {
      setValue('company.name', company.name, { shouldDirty: true })
      markFieldAsPrefilled('company.name')
    }

    if (company.street) {
      setValue('company.street', company.street, { shouldDirty: true })
      markFieldAsPrefilled('company.street')
    }

    if (company.house_number) {
      setValue('company.house_number', company.house_number, { shouldDirty: true })
      markFieldAsPrefilled('company.house_number')
    }

    if (company.house_number_addition) {
      setValue('company.house_number_addition', company.house_number_addition, { shouldDirty: true })
      markFieldAsPrefilled('company.house_number_addition')
    }

    if (company.postal_code) {
      setValue('company.postal_code', company.postal_code, { shouldDirty: true })
      markFieldAsPrefilled('company.postal_code')
    }

    if (company.city) {
      setValue('company.city', company.city, { shouldDirty: true })
      markFieldAsPrefilled('company.city')
    }

    if (company.kvk_number) {
      setValue('company.coc', company.kvk_number, { shouldDirty: true })
      markFieldAsPrefilled('company.coc')
    }

    // Force re-render by resetting form with all current values
    setTimeout(() => {
      const currentValues = getValues()
      reset(currentValues, { keepDirty: true })
    }, 10)

    // Clear search
    setInputValue('')
    setSearchQuery('')
    setSelectedValue(null)
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Autocomplete
        options={kvk.list || []}
        value={selectedValue}
        loading={kvk.loading}
        inputValue={inputValue}
        onInputChange={(_, newInputValue, reason) => {
          // Only update if it's an actual input change, not a reset
          if (reason === 'input') {
            setInputValue(newInputValue)
          } else if (reason === 'clear') {
            setInputValue('')
          }
        }}
        onChange={(_, value) => handleSelectCompany(value)}
        getOptionLabel={(option: IKvkCompany) => option.name || ''}
        isOptionEqualToValue={(option: IKvkCompany, value: IKvkCompany) => option.kvk_number === value.kvk_number}
        filterOptions={x => x} // Disable built-in filtering since we search via API
        renderInput={params => (
          <TextField
            {...params}
            label="Zoek bedrijf in KVK (naam of KVK-nummer)"
            placeholder="Typ om te zoeken..."
            InputProps={{
              ...params.InputProps,
              startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              endAdornment: (
                <>
                  {kvk.loading ? (
                    <CircularProgress
                      color="inherit"
                      size={20}
                    />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option: IKvkCompany) => (
          <Box
            component="li"
            {...props}>
            <Stack
              spacing={0.5}
              sx={{ width: '100%' }}>
              <Typography
                variant="body1"
                fontWeight={500}>
                {option.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary">
                KVK: {option.kvk_number}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary">
                {option.street} {option.house_number}
                {option.house_number_addition ? ` ${option.house_number_addition}` : ''}, {option.postal_code}{' '}
                {option.city}
              </Typography>
            </Stack>
          </Box>
        )}
        noOptionsText={
          inputValue.length < 2
            ? 'Typ minimaal 2 karakters om te zoeken'
            : kvk.loading
            ? 'Zoeken...'
            : 'Geen resultaten gevonden'
        }
        clearOnBlur
        clearOnEscape
      />

      {/* Show default helper text when not showing other messages */}
      {(inputValue.length < 2 || kvk.loading) && kvk.list.length === 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}>
          Of vul de gegevens handmatig in onderstaande velden
        </Typography>
      )}
    </Box>
  )
})
